import React from 'react';
import { Box, Container, Typography, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import RequireAuth from '../components/athletes/RequireAuth';

/**
 * Getting Started — a launchpad, not a gate. Reached exactly once, as the
 * explicit destination of a fresh sign-up (see AuthModal's signUpRedirectUrl/
 * googleRedirectUrl), never enforced or re-checked afterward: there is no
 * completion flag, no relationship requirement, and nothing anywhere in the
 * app routes a user back here or blocks them from going anywhere else. A
 * user can also simply navigate here directly at any time (e.g. a stale
 * /onboarding bookmark) — that's fine; it's an ordinary page, not access-
 * controlled beyond the normal "must be signed in" RequireAuth wrapper every
 * other athlete/team page already uses.
 *
 * Replaces the old role-questionnaire onboarding (Parent/Guardian, Coach,
 * Athlete → user_roles) entirely. That table and the useUserRoles hook are
 * deliberately left in place, untouched, and simply unused by this page —
 * see the product decision this supersedes for why: user_roles was always
 * documented as a UX/routing signal only, never an authorization input, so
 * removing it from routing has no security effect, and a possible future
 * cleanup pass can retire the table on its own, separately from this change.
 *
 * Each card is a direct action — no checkboxes, no Continue button, no
 * completion summary. A guardian arriving via a shared /join/:code invite
 * link never sees this page at all (that route's own auth handling always
 * preserves the invite URL instead of ever redirecting here).
 */
function GettingStartedInner() {
  const navigate = useNavigate();

  const cards = [
    {
      key: 'add-athlete',
      title: 'Add an Athlete',
      desc: 'Track yourself or an athlete you manage.',
      onClick: () => navigate('/athletes/new'),
    },
    {
      key: 'find-team',
      title: 'Find a Team',
      badge: 'COMING SOON',
      desc: "Search for your athlete's team and request access.",
      disabled: true,
    },
    {
      key: 'access-code',
      title: 'Enter Access Code',
      desc: 'Have a team access code? Enter it here to request access.',
      onClick: () => navigate('/athletes/join-team'),
    },
    {
      // Legacy standalone Team creation (/my-teams/new) is no longer
      // exposed in normal UI (030 cleanup) — current product hierarchy is
      // Program -> Season Team -> Team Hub, so onboarding now points at
      // the existing Program area (/my-programs) rather than inventing a
      // new Program-creation workflow. /my-programs itself has no visible
      // "create Program" action yet as of this change — a separate, pre-
      // existing gap, not addressed here.
      key: 'create-program',
      title: 'Create a Program',
      desc: 'Set up your organization, then create Season Teams for each grade, age group, or division.',
      onClick: () => navigate('/my-programs'),
    },
  ];

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, textAlign: 'center' }}>
        What would you like to do?
      </Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 4, textAlign: 'center' }}>
        Pick a starting point — you can always do the others later.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {cards.map(c => (
          <Card
            key={c.key}
            onClick={c.disabled ? undefined : c.onClick}
            sx={{
              backgroundColor: 'rgba(10,20,34,0.8)',
              border: '1px solid #1e2a3a',
              cursor: c.disabled ? 'default' : 'pointer',
              opacity: c.disabled ? 0.55 : 1,
              '&:hover': c.disabled ? {} : { borderColor: 'rgba(46,168,75,0.4)' },
            }}
          >
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{c.title}</Typography>
                {c.badge && (
                  <Typography sx={{
                    fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.06em',
                    color: '#f5a623', border: '1px solid rgba(245,166,35,0.4)',
                    borderRadius: 1, px: 0.75, py: 0.15,
                  }}>
                    {c.badge}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ color: '#9eb8d0', fontSize: '0.9rem' }}>{c.desc}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Container>
  );
}

export default function GettingStartedPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth>
        <GettingStartedInner />
      </RequireAuth>
    </Box>
  );
}
